import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {CrossAuthService} from "../_services/cross-auth.service";
import {LoaderService} from "../_services/loader.service";
import {AlertService} from "../_services/alert.service";
import {HttpErrorResponse} from "@angular/common/http";
import {UserInfoService} from "../_services/user-info.service";
import {TokenService} from "../_services/token.service";
import {CrossAuthStateService} from "../_services/cross-auth-state.service";

@Component({
  selector: 'app-cross-auth',
  templateUrl: './cross-auth.component.html',
  styleUrls: ['./cross-auth.component.css']
})
export class CrossAuthComponent implements OnInit, OnDestroy {
  
  /** Режим работы компонента: 'cross-auth' или 'cross-login' */
  mode: 'cross-auth' | 'cross-login' = 'cross-auth';
  
  /** Флаг успешной авторизации */
  authenticated = false;
  
  /** Сообщение о статусе */
  statusMessage = 'Ожидание соединения с внешним приложением...';
  
  private messageHandler: (event: MessageEvent) => void;
  private pingInterval: any;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private crossAuthService: CrossAuthService,
              private userInfoService: UserInfoService,
              private tokenService: TokenService,
              private crossAuthStateService: CrossAuthStateService,
              private loaderService: LoaderService,
              private alertService: AlertService) { }

  ngOnInit() {
    // Определяем режим по наличию параметров маршрута
    this.route.params
      .subscribe(
        params => {
          if (params['userId'] && params['token']) {
            // Режим cross-login: /cross-login/:userId/:token
            this.mode = 'cross-login';
            const userId = parseInt(params['userId']);
            const token = params['token'];
            this.crossLogin(userId, token);
          } else {
            // Режим cross-auth: /cross-auth или /cross-auth?success=true
            this.mode = 'cross-auth';
            
            // Проверяем параметр success
            this.route.queryParams.subscribe(queryParams => {
              if (queryParams['success'] === 'true' && this.crossAuthStateService.isCrossAuthEnabled()) {
                this.statusMessage = 'Авторизация выполнена';
                this.authenticated = true;
                // Отправляем ответ открывшему окно (возврат после логина)
                const origin = this.crossAuthStateService.getOrigin();
                this.sendAuthResponse({ success: true }, origin);
                this.crossAuthStateService.disableCrossAuth();
              } else {
                // Сбрасываем режим внешней авторизации при обновлении страницы
                this.crossAuthStateService.disableCrossAuth();
                this.initCrossAuthListener();
              }
            });
          }
        },
        e => console.error(e)
      );
  }

  ngOnDestroy() {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  }

  /**
   * Инициализация слушателя сообщений для режима cross-auth
   */
  private initCrossAuthListener(): void {
    this.messageHandler = (event: MessageEvent) => {
      // Проверяем наличие данных
      if (!event.data || !event.data.type) {
        return;
      }

      // Ожидаем сообщение с токеном
      if (event.data.type === 'CROSS_AUTH_TOKEN') {
        const token = event.data.token;
        const origin = event.origin;

        if (!token) {
          this.sendAuthResponse({ success: false, error: 'Токен не предоставлен' }, origin);
          return;
        }

        this.handleExternalToken(token, origin);
      }
    };

    window.addEventListener('message', this.messageHandler);

    // Периодически отправляем сообщение о готовности принимать токен
    this.pingInterval = setInterval(() => {
      // Останавливаем если включен режим внешней авторизации или авторизация уже завершена
      if (this.crossAuthStateService.isCrossAuthEnabled() || this.authenticated) {
        clearInterval(this.pingInterval);
        return;
      }
      if (window.opener) {
        window.opener.postMessage({ type: 'CROSS_AUTH_READY' }, '*');
        console.log('[CrossAuth] window.opener: отправлено CROSS_AUTH_READY');
      } else {
        console.log('[CrossAuth] window.opener: отсутствует (окно не открыто как popup)');
      }
    }, 500);
  }

  /**
   * Обработка токена от внешнего приложения
   */
  private handleExternalToken(token: string, origin: string): void {
    this.statusMessage = 'Проверка токена...';
    this.loaderService.show();
    this.crossAuthStateService.enableCrossAuth(origin, token);

    this.userInfoService.setup()
      .then(() => {
        this.loaderService.hide();
        this.sendAuthResponse({ success: true }, origin);
        this.statusMessage = 'Авторизация выполнена';
        this.authenticated = true;
      })
      .catch(() => {
        this.loaderService.hide();
        this.router.navigate(['/login']);
      });
  }

  /**
   * Отправить ответ внешнему приложению
   */
  private sendAuthResponse(data: any, origin: string): void {
    const payload = { type: 'CROSS_AUTH_RESPONSE', ...data };
    if (window.opener) {
      window.opener.postMessage(payload, origin);
      console.log('[CrossAuth] window.opener: отправлен CROSS_AUTH_RESPONSE', { origin, success: data.success, hasError: !!data.error });
    } else {
      console.warn('[CrossAuth] window.opener: отсутствует, ответ не отправлен', { origin, payload });
    }
  }

  /**
   * Обработка cross-login (существующая логика для /cross-login/:userId/:token)
   */
  private crossLogin(userId: number, token: string): void {
    this.loaderService.show();
    this.loaderService.showText('Вход в панель управления...');

    this.crossAuthService
      .crossLogin(userId, token)
      .subscribe(
        () => {
          this.userInfoService.setup().then(() => {
            // Проверяем режим внешней авторизации
            if (this.crossAuthStateService.isCrossAuthEnabled()) {
              // Отправляем уведомление об успешной авторизации
              this.crossAuthStateService.sendMessage({ 
                type: 'CROSS_AUTH_RESPONSE', 
                success: true 
              });
              // Отключаем режим внешней авторизации
              this.crossAuthStateService.disableCrossAuth();
              // Показываем сообщение об успешной авторизации
              this.statusMessage = 'Авторизация выполнена';
              this.authenticated = true;
            } else {
              this.router.navigate(['/']);
            }
            
            this.loaderService.hide();
          });
        },
        r => {
          if(r instanceof HttpErrorResponse) {
            switch(r.status) {
              case 404:
                this.alertService.error('Пользователя не существует. Выполните повторный вход в систему.', true);
                break;
              case 403:
                this.alertService.error('Токен авторизации просрочен. Выполните повторный вход в систему.', true);
                break;
              default:
                this.alertService.error('Возникла непредвиденная ошибка. Выполните повторный вход в систему.', true);
                break;
            }
            this.loaderService.hide();
            this.router.navigate(['/login']);
          }
        }
      );
  }
}
